import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { useWorkspace } from '@/context/WorkspaceContext'
import { RemoteConfigContext } from '@/context/RemoteConfigContext'
import { Lock, ChevronLeft } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { HomeIcon } from '@/components/icons/Home'
import { ChatsIcon } from '@/components/icons/Chats'
import { SuggestionsIcon } from '@/components/icons/Suggestions'
import { MeetingsIcon } from '@/components/icons/Meetings'
import { TeamIcon } from '@/components/icons/Team'
import { IntegrationsIcon } from '@/components/icons/Integrations'
import { ProjectsIcon } from '@/components/icons/Projects'
import { TeamSettingsIcon } from '@/components/icons/Settings'
import { ProductsIcon } from '@/components/icons/Products'
import { CompetitorsIcon } from '@/components/icons/Competitors'
export function SidebarMenu({ selectedMenu, setSelectedMenu, isOnboarding, isCollapsed }) {
  const { config } = useContext(RemoteConfigContext)
  const { isPartner } = useWorkspace()

  // Helper function to determine if a menu item should be disabled during onboarding
  const getMenuItemProps = (menuKey) => {
    const isDisabled = isOnboarding && menuKey !== 'home-new' && menuKey !== 'home'
    const baseOpacity = isDisabled ? 'opacity-40' : ''
    const basePointerEvents = isDisabled ? 'pointer-events-none' : ''

    return {
      className: `h-12 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between w-full'} ${baseOpacity} ${basePointerEvents}`,
      onClick: isDisabled ? undefined : () => setSelectedMenu(menuKey),
      disabled: isDisabled,
    }
  }

  // Helper component to wrap menu items with tooltips when disabled
  const MenuItemWrapper = ({ children, isDisabled }) => {
    if (isDisabled) {
      return (
        <TooltipProvider delayDuration={0} skipDelayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>{children}</div>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-black text-white border-black">
              <p>Complete setup to unlock</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }
    return children
  }

  // Helper component for menu items with consistent tooltip behavior
  const MenuItem = ({ menuKey, icon, label, isDisabled = false }) => {
    const isActive = selectedMenu === menuKey
    const props = getMenuItemProps(menuKey)

    const content = (
      <button {...props}>
        <div className={`flex items-center ${isCollapsed ? '' : 'flex-1'}`}>
          {icon}
          {!isCollapsed &&
            (isActive ? (
              <p className="text-black dark:text-white text-base font-semibold font-['Manrope'] mx-4">{label}</p>
            ) : (
              <p className="text-slate dark:text-gray-400 text-base font-medium font-['Manrope'] mx-4">{label}</p>
            ))}
        </div>
        {!isCollapsed && isDisabled && <Lock className="w-4 h-4 text-gray-400 flex-shrink-0 mr-4" />}
      </button>
    )

    if (isCollapsed) {
      return (
        <TooltipProvider delayDuration={0} skipDelayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            <TooltipContent side="right" className="bg-black text-white border-black">
              <p>{label}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }

    return content
  }

  const settingsMenus = ['team-settings', 'products', 'competitors', 'integrations', 'team']
  const isInSettings = settingsMenus.includes(selectedMenu)

  return (
    <div className={isCollapsed ? 'flex flex-col items-center' : 'ml-8'}>
      {isInSettings && (
        <>
          <TooltipProvider delayDuration={0} skipDelayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setSelectedMenu('home-new')}
                  className={`mt-14 mb-4 flex items-center ${isCollapsed ? 'justify-center' : ''}`}
                >
                  <ChevronLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  {!isCollapsed && (
                    <p className="text-slate dark:text-gray-400 text-base font-medium font-['Manrope'] leading-[23px] mx-4">
                      Back to app
                    </p>
                  )}
                </button>
              </TooltipTrigger>
              {isCollapsed && (
                <TooltipContent side="right" className="bg-black text-white border-black">
                  <p>Back to app</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          <MenuItemWrapper menuKey="team-settings" isDisabled={isOnboarding}>
            <MenuItem
              menuKey="team-settings"
              label="Preferences"
              isDisabled={isOnboarding}
              icon={<TeamSettingsIcon isSelected={selectedMenu === 'team-settings'} />}
            />
          </MenuItemWrapper>

          <MenuItemWrapper menuKey="products" isDisabled={isOnboarding}>
            <MenuItem
              menuKey="products"
              label="Products"
              isDisabled={isOnboarding}
              icon={<ProductsIcon isSelected={selectedMenu === 'products'} />}
            />
          </MenuItemWrapper>

          {/* Competitors (under Settings) */}
          <MenuItemWrapper menuKey="competitors" isDisabled={isOnboarding}>
            <MenuItem
              menuKey="competitors"
              label="Competitors"
              isDisabled={isOnboarding}
              icon={<CompetitorsIcon isSelected={selectedMenu === 'competitors'} />}
            />
          </MenuItemWrapper>

          <MenuItemWrapper menuKey="integrations" isDisabled={isOnboarding}>
            <MenuItem
              menuKey="integrations"
              label="Integrations"
              isDisabled={isOnboarding}
              icon={<IntegrationsIcon isSelected={selectedMenu === 'integrations'} />}
            />
          </MenuItemWrapper>

          <MenuItemWrapper menuKey="team" isDisabled={isOnboarding}>
            <MenuItem
              menuKey="team"
              label="Team"
              isDisabled={isOnboarding}
              icon={<TeamIcon isSelected={selectedMenu === 'team'} />}
            />
          </MenuItemWrapper>
        </>
      )}

      {!isInSettings && (
        <>
          <div className="mt-12">
            <MenuItem
              menuKey="home-new"
              label={isOnboarding ? 'Welcome' : 'Home'}
              icon={<HomeIcon isSelected={selectedMenu === 'home-new'} />}
            />
          </div>

          <MenuItemWrapper menuKey="chats" isDisabled={isOnboarding}>
            <MenuItem
              menuKey="chats"
              label="Chats"
              isDisabled={isOnboarding}
              icon={<ChatsIcon isSelected={selectedMenu === 'chats'} />}
            />
          </MenuItemWrapper>

          {/* Only show Roadmap button for partner workspaces */}
          {config.roadmap && isPartner && (
            <MenuItemWrapper menuKey="roadmap" isDisabled={isOnboarding}>
              <MenuItem
                menuKey="roadmap"
                label="Suggestions"
                isDisabled={isOnboarding}
                icon={<SuggestionsIcon isSelected={selectedMenu === 'roadmap'} />}
              />
            </MenuItemWrapper>
          )}

          <MenuItemWrapper menuKey="meetings" isDisabled={isOnboarding}>
            <MenuItem
              menuKey="meetings"
              label="Meetings"
              isDisabled={isOnboarding}
              icon={<MeetingsIcon isSelected={selectedMenu === 'meetings'} />}
            />
          </MenuItemWrapper>

          <MenuItemWrapper menuKey="projects" isDisabled={isOnboarding}>
            <MenuItem
              menuKey="projects"
              label="Projects"
              isDisabled={isOnboarding}
              icon={<ProjectsIcon isSelected={selectedMenu === 'projects'} />}
            />
          </MenuItemWrapper>

          {/* Chats was previously gated by Slack connection; now it is always available and positioned under Home */}

          {/* Only show Recruiting button for partner workspaces */}
          {/* {isPartner && (
        <button onClick={() => setSelectedMenu('recruiting')} className="mt-6 flex items-center">
          <People
            className={cn('w-[23px] h-[23px]', selectedMenu !== 'recruiting' ? 'fill-slate' : 'fill-pink')}
            withFill={false}
          />
          <p
            className={cn(
              "text-base font-['Manrope'] mx-4",
              selectedMenu !== 'recruiting'
                ? 'text-slate font-medium leading-[23px]'
                : 'text-black font-semibold leading-tight'
            )}
          >
            Recruiting
          </p>
        </button>
      )} */}
        </>
      )}
    </div>
  )
}

SidebarMenu.propTypes = {
  selectedMenu: PropTypes.string.isRequired,
  setSelectedMenu: PropTypes.func.isRequired,
  isOnboarding: PropTypes.bool.isRequired,
  isCollapsed: PropTypes.bool,
}
